import { useMessage } from 'naive-ui'
import { shopAddcar, shopCarbuynow, shopCarlist } from '~/services/api'

/**
 * 组合式 购物车函数
 */
export function useCart() {
  const { t } = useI18n()

  const message = useMessage()

  const { count } = storeToRefs(useShopCart())

  /**  立即购买按钮状态 */
  const buynowloading = ref(false)

  /**  购物车列表 */
  const cartList = ref<API.Cart>()

  /**  加载状态 */
  const loading = ref(false)

  /**  选择商品 */
  const cartIds = computed(() => cartList.value?.line_items?.filter(item => item.isSelected).map(item => item.id) ?? [])

  /**  商品列表 */
  const productList = computed(() => cartList.value?.line_items ?? [])

  // 监听carList值的变化，并在变化时执行回调函数
  watchEffect(() => {
    count.value = productList.value.reduce((total, item) => total + +item.quantity, 0)
  })

  /**  获取购物车列表 */
  const getcarlist = async () => {
    loading.value = true
    try {
      const { data } = await shopCarlist({ source: 'xfproduct' })
      if (data?.cart) {
        cartList.value = data.cart

        // 默认选中
        cartList.value?.line_items?.forEach((item) => {
          item.isSelected = true
        })
      }
    }
    finally {
      loading.value = false
    }
    handleAllChecked()
  }

  /**  商品全选 */
  const handleAllChecked = () => {
    cartList.value?.line_items?.forEach((item) => {
      item.isSelected = true
    })
  }

  /**  购物车增加减少 */
  const changeGoodsNum = async (item: any, value: any) => {
    item.quantity = Number(item.quantity) + value
    debouncedPostCartData(item, value)
  }

  /**  传输购物车数据 */
  const postCartData = async (item: any, value: any) => {
    await shopAddcar({
      source: '',
      proid: item.product_id,
      car_id: item.id,
      varid: item.variant_id,
      num: item.quantity,
    })
    await getcarlist()
    useTarck('event', {
      sact: Number(value) > 0 ? 'add_to_cart' : 'remove_from_cart',
      proid: item.product_id,
      varid: item.variant_id,
      num: item.quantity,
    })
  }

  /**  防抖函数 */
  const debouncedPostCartData: (item: any, value: any) => Promise<any> = useDebounceFn(postCartData, 150)

  /**  立即购买 */
  const buyNow = async () => {
    if (productList.value?.length) {
      if (!cartIds.value.length) {
        return message.info(t('headers.selectError') as string)
      }
      const data = {
        source: '',
        vids: cartIds.value.join(','),
      }
      buynowloading.value = true
      try {
        const { data: result }: any = await shopCarbuynow(data)
        useTarck('event', {
          sact: 'buy_it_now',
        })
        if (result?.code == 200 && result?.state == 'ok') {
          navigateTo(`/checkouts/${result?.okey}`)
        }
        return
      }
      catch (error) {
        console.error(error)
      }
      finally {
        buynowloading.value = false
      }
    }
    message.error('The shopping cart is empty')
  }

  /**  计算购物车总价 */
  const totalPrice = computed(() => {
    /**  正常的价格 */
    return productList.value.filter(item => item.isSelected && !item.pici).reduce((total, item) => total + item.quantity * +item.price, 0) + (+bundleTotalPriceValue.value - bundleSave.value)
  })

  /**  删除商品 */
  const removeCar = async (item: any) => {
    loading.value = true
    try {
      await postCartData({ ...item, quantity: 0 }, 0)
    }
    catch (error) {
      console.error(error)
    }
    finally {
      loading.value = false
    }
  }

  // #region 捆绑销售逻辑

  /**  计算捆绑商品分组 */
  const bundleGroup = computed(() => {
    return productList.value?.reduce((acc: {
      [key: string]: API.LineItems[]
    }, item) => {
      const key = item.pici
      if (!key)
        return acc
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(item)
      return acc
    }, {})
  })

  /**  捆绑商品价格信息汇总 */
  const bundleTotalPrice = computed(() => {
    return Object.keys(bundleGroup.value).reduce((acc: { [key: string]: { totalPrice: number, totalDiscount: number } }, pici) => {
      const variants = bundleGroup.value[pici]

      // 判断当前分组是否所有商品都被选中
      const allSelected = variants.every(item => item.isSelected)
      const summary = variants.reduce((summaryAcc, item) => {
        if (item.isSelected) {
          summaryAcc.totalPrice += item.cost_price + Number.parseFloat(item.price)
        }
        if (allSelected) {
          summaryAcc.totalDiscount += item.cost_price
        }
        return summaryAcc
      }, {
        totalPrice: 0,
        totalDiscount: 0,
      })
      acc[pici] = summary
      return acc
    }, {})
  })

  /**  捆绑商品的总价格 */
  const bundleTotalPriceValue = computed(() => {
    return Object.keys(bundleTotalPrice.value)?.reduce((acc, pici) => {
      const { totalPrice } = bundleTotalPrice.value[pici]
      acc += totalPrice
      return acc
    }, 0)
  })

  /**  捆绑商品总节省金额 */
  const bundleSave = computed(() => {
    return Object.keys(bundleTotalPrice.value)?.reduce((acc, pici) => {
      const { totalDiscount } = bundleTotalPrice.value[pici]
      acc += totalDiscount
      return acc
    }, 0)
  })

  /**  捆绑商品id集合 */
  const bundleIds = computed(() => {
    return Object.keys(bundleGroup.value)?.reduce((acc, pici) => {
      const variants = bundleGroup.value[pici]
      // 判断当前分组是否所有商品都被选中
      const allSelected = variants.every(item => item.isSelected)
      if (allSelected) {
        variants.forEach((item) => {
          acc.push(item.id)
        })
      }
      return acc
    }, [] as number[])
  })

  // #endregion

  return {
    bundleIds,
    bundleTotalPriceValue,
    bundleSave,
    bundleTotalPrice,
    bundleGroup,
    /**  删除商品 */
    removeCar,
    /** 计算购物车总价  */
    cartotalprice: totalPrice,
    /**  立即购买 */
    buyNow,
    /**  立即购买按钮状态 */
    buynowloading,
    /**  购物车增加减少 */
    changeGoodsNum,

    /**  购物车列表 */
    carList: cartList,
    /**  列表加载状态 */
    loading,
    /**  选择商品id */
    carIds: cartIds,
    /**  获取购物车列表 */
    getcarlist,
  }
}
